import { toast } from 'react-toastify';
import fetcher from '../../lib/fetcher';
import useSWRMutation from 'swr/mutation';
import { MarketTypes } from 'api/types';

export enum EDealRequestType {
  Purchase = 'Purchase',
  Sale = 'Sale',
  DealService = 'DealService',
  Other = 'Other',
  Ransom = 'Ransom',
  Estimate = 'Estimate',
}
export interface IDealRansomRequestOutOfSystem {
  marketType: MarketTypes;
  type: EDealRequestType.Ransom;
  ransom: {
    externalFacility: {
      streetId?: number;
      complexId?: number;
      areaInSqM: number;
      buildingId: number;
      typeId?: number;
    };
    estimatedPrice?: number;
    apartmentNumber: string;
  };
}
export interface IDealRansomRequestInSystem {
  marketType: MarketTypes;
  type: EDealRequestType.Ransom;
  ransom: {
    apartmentNumber: string;
    facilityId: number;
  };
}
export interface IDealEstimateRequestOutOfSystem {
  marketType: MarketTypes;
  type: EDealRequestType.Estimate;
  estimate: {
    externalFacility: {
      streetId?: number;
      complexId?: number;
      areaInSqM: number;
      buildingId: number;
      typeId?: number;
    };
  };
}
export interface IDealEstimateRequestInSystem {
  marketType: MarketTypes;
  type: EDealRequestType.Estimate;
  estimate: {
    facilityId: number;
  };
}

export interface IDealRequestCommonSchema {
  streetId?: number;
  complexId?: number;
  areaInSqM?: number;
  buildingId?: number;
  typeId?: number;
  facilityId?: number;
  apartmentNumber?: string;
  estimatedPrice?: number;
}

const ERROR_TEXT = 'Что то не так перепроверьте заполненые поля';

const useDealRequest = () => {
  async function dealRequest(
    url: string,
    {
      arg,
    }: {
      arg: {
        type: EDealRequestType;
        mode: 'inSystem' | 'outOfSystem';
        data: IDealRequestCommonSchema;
        marketType: MarketTypes;
      };
    },
  ) {
    try {
      let request: unknown | undefined = undefined;

      if (arg.type === EDealRequestType.Ransom && arg.mode === 'outOfSystem') {
        const {
          areaInSqM,
          buildingId,
          apartmentNumber,
          streetId,
          complexId,
          typeId,
          estimatedPrice,
        } = arg.data;
        if (areaInSqM && buildingId && apartmentNumber) {
          const ransomReq: IDealRansomRequestOutOfSystem = {
            marketType: arg.marketType,
            type: EDealRequestType.Ransom,
            ransom: {
              externalFacility: {
                streetId: streetId ?? undefined,
                complexId: complexId ?? undefined,
                areaInSqM: areaInSqM,
                buildingId: buildingId,
                typeId: typeId ?? undefined,
              },
              estimatedPrice: estimatedPrice ?? undefined,
              apartmentNumber: String(apartmentNumber),
            },
          };
          request = ransomReq;
        }
      }

      if (arg.type === EDealRequestType.Ransom && arg.mode === 'inSystem') {
        const { facilityId, apartmentNumber } = arg.data;
        if (facilityId && apartmentNumber) {
          const ransomReq: IDealRansomRequestInSystem = {
            marketType: arg.marketType,
            type: EDealRequestType.Ransom,
            ransom: {
              facilityId: facilityId,
              apartmentNumber: apartmentNumber,
            },
          };
          request = ransomReq;
        }
      }

      if (arg.type === EDealRequestType.Estimate && arg.mode === 'outOfSystem') {
        const { areaInSqM, buildingId, streetId, complexId, typeId, estimatedPrice } = arg.data;
        if (areaInSqM && buildingId) {
          const estimateReq: IDealEstimateRequestOutOfSystem = {
            marketType: arg.marketType,
            type: EDealRequestType.Estimate,
            estimate: {
              externalFacility: {
                complexId: complexId ?? undefined,
                streetId: streetId ?? undefined,
                areaInSqM: areaInSqM,
                buildingId: buildingId,
                typeId: typeId ?? undefined,
              },
            },
          };
          request = estimateReq;
        }
      }

      if (arg.type === EDealRequestType.Estimate && arg.mode === 'inSystem') {
        const { facilityId } = arg.data;
        if (facilityId) {
          const estimateReq: IDealEstimateRequestInSystem = {
            marketType: arg.marketType,
            type: EDealRequestType.Estimate,
            estimate: {
              facilityId: facilityId,
            },
          };
          request = estimateReq;
        }
      }

      if (request) {
        const data = await fetcher.swr(url, {
          method: 'POST',
          data: request,
        });
        return data.result;
      } else {
        toast.error(ERROR_TEXT);
        throw new Error(ERROR_TEXT);
      }
    } catch (error: any) {
      toast.error(`Что то не так. Проверьте заполненные данные. Причина: ${error}`);
      throw new Error(error);
    }
  }

  const { data, error, isMutating, trigger } = useSWRMutation('/deal-requests', dealRequest);

  return { data: data, error, isMutating, trigger };
};

export default useDealRequest;
